import { useEffect, useState } from "react";
import { ApiError, createScript, getScriptQuota, type ScriptQuota } from "../api";
import { unlockAudio } from "../audio";
import { Sheet } from "../ui";
import { btnShu } from "./Cram";
import { SubscribeOffer } from "./Paywall";

const MAX_CHARS = 1000;

export function ScriptSheet({
  open,
  onClose,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: (id: string) => void;
}) {
  const [text, setText] = useState("");
  const [title, setTitle] = useState("");
  const [quota, setQuota] = useState<ScriptQuota | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) getScriptQuota().then(setQuota, () => setQuota(null));
  }, [open]);

  const full = quota !== null && quota.used >= quota.limit;

  const submit = async () => {
    unlockAudio();
    setBusy(true);
    setError(null);
    try {
      const { id } = await createScript({ text: text.trim(), ...(title.trim() && { title: title.trim() }) });
      setText("");
      setTitle("");
      onCreated(id);
    } catch (e) {
      setError(
        e instanceof ApiError && e.status === 429
          ? "今月の上限に達しました。"
          : e instanceof ApiError && e.status === 400
            ? "英文を 1000 文字以内で入力してください。"
            : e instanceof Error
              ? e.message
              : String(e),
      );
      getScriptQuota().then(setQuota, () => {});
    } finally {
      setBusy(false);
    }
  };

  return (
    <Sheet open={open} title="自分の原稿で練習する" onClose={onClose}>
      <p className="text-[12px] leading-relaxed text-ink-2">
        面接の自己紹介、発表原稿、会議で言う一言など、本番で言う英語を貼ってください。区切りは自動で付き、あとから直せます。音声の準備に 30 秒ほどかかります。
      </p>
      <input
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        maxLength={80}
        placeholder="タイトル(任意)"
        className="mt-3 w-full border-2 border-ink bg-paper px-3 py-2 text-[14px]"
      />
      <textarea
        value={text}
        onChange={(e) => setText(e.target.value.slice(0, MAX_CHARS))}
        rows={7}
        placeholder="I'd like to walk you through our results for this quarter."
        className="mt-2 w-full border-2 border-ink bg-paper px-3 py-2 text-[15px] leading-relaxed"
      />
      <div className="mt-1 flex justify-between text-[11px] text-ink-3">
        <span>{quota && `${quota.entitled ? "今月" : "無料枠"} ${quota.used}/${quota.limit} 本`}</span>
        <span>
          {text.length}/{MAX_CHARS}
        </span>
      </div>
      {full && !quota.entitled ? (
        <div className="mt-4">
          <p className="mb-3 text-[13px] font-bold">無料枠の 1 本を使いました。使い放題プランなら月 30 本まで作れます。</p>
          <SubscribeOffer />
        </div>
      ) : (
        <button type="button" onClick={submit} disabled={busy || full || !text.trim()} className={`${btnShu} mt-3 w-full`}>
          {busy ? "準備中…" : "▶ この原稿で練習する"}
        </button>
      )}
      {error && <p className="mt-2 text-[12px] font-bold text-shu">{error}</p>}
    </Sheet>
  );
}
